import { useEffect, useState } from "react";
import { API } from "../App";
import { Button } from "./ui/button";
import { Check, Loader2, Users2, Save } from "lucide-react";
import { toast } from "sonner";

/**
 * Inline editor for a customer's segment memberships.
 * Used inside AdminCustomers' profile drawer.
 */
export default function CustomerSegmentsEditor({ userId, value, onUpdated }) {
  const [segments, setSegments] = useState([]);
  const [selected, setSelected] = useState(value || []);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setSelected(value || []); }, [value, userId]);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/admin/segments`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => alive && setSegments(Array.isArray(data) ? data : []))
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, []);

  const toggle = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const save = async () => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/admin/users/${userId}/segments`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ segment_ids: selected }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || "Failed");
      }
      toast.success("Segments updated");
      onUpdated?.(selected);
    } catch (e) {
      toast.error(e.message || "Failed to update segments");
    } finally {
      setSaving(false);
    }
  };

  const currentSet = new Set(value || []);
  const selectedSet = new Set(selected);
  const dirty = currentSet.size !== selectedSet.size || [...selectedSet].some((s) => !currentSet.has(s));

  return (
    <div data-testid="customer-segments-editor">
      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
        <Users2 size={16} className="text-green-600" /> Customer Segments
      </h3>
      {loading ? (
        <p className="text-xs text-gray-400">Loading segments…</p>
      ) : segments.length === 0 ? (
        <p className="text-xs text-gray-400 italic">No segments defined yet. Create some under <span className="font-medium">Admin → Customer Segments</span>.</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-1.5" data-testid="customer-segments-options">
            {segments.map((s) => {
              const active = selectedSet.has(s.segment_id);
              return (
                <button
                  type="button"
                  key={s.segment_id}
                  onClick={() => toggle(s.segment_id)}
                  className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-all ${
                    active
                      ? "bg-green-600 text-white border-green-600"
                      : "bg-white text-gray-600 border-gray-200 hover:border-green-400 hover:text-green-700"
                  }`}
                  data-testid={`customer-segments-option-${s.segment_id}`}
                >
                  {active && <Check size={12} />}
                  {s.name}
                </button>
              );
            })}
          </div>
          <div className="mt-3">
            <Button
              type="button"
              size="sm"
              disabled={!dirty || saving}
              onClick={save}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50"
              data-testid="customer-segments-save"
            >
              {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</> : <><Save size={14} className="mr-1.5" /> Save Segments</>}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
